package com.appsyde.shared.presentation.style.style

import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.token.AppStylesTokens
import org.jetbrains.compose.web.css.*

object AppStylesTicketsSidebar : StyleSheet() {

    // Espacements : un peu plus serrés entre les blocs
    private val gapSmall = 8.px
    private val gapBlock = 26.px          // entre chaque bloc
    private val gapBeforeButton = 28.px
    private val gapSpacer = 18.px         // spacer vertical avant le footer

    val card by style {
        padding(18.px)
        borderRadius(18.px)
        backgroundColor(rgba(255, 255, 255, 0.85))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        property("box-shadow", "0 12px 28px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.75)")
        property("backdrop-filter", "blur(10px)")
        property("-webkit-backdrop-filter", "blur(10px)")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        property("transition", "transform 160ms ease, box-shadow 160ms ease")
    }

    val cardHeader by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
    }

    val kicker by style {
        fontSize(11.px)
        fontWeight(700)
        color(Color("rgba(102,96,230,0.85)"))
    }

    val title by style {
        fontSize(13.px)
        fontWeight(750)
        color(Color(AppStylesTokens.INK_SOFT))
    }

    /** Conteneur unique : une seule card pour tout le dossier */
    val dossierCardSingle by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        minWidth(0.px)
    }

    /** En-tête : ligne titre + cercle assignee à droite */
    val dossierHeaderBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(3.px)
        paddingBottom(0.px)
        marginBottom(0.px)
    }

    /** Ligne : titre à gauche, cercle initiales à droite */
    val dossierHeaderTitleRow by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
    }

    val dossierHeaderTitleFlex by style {
        flex(1)
        minWidth(0.px)
    }

    /** Cercle initiales responsable (sidebar) — plus gros que dans la row */
    val dossierHeaderAssigneeCircle by style {
        width(36.px)
        height(36.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(102,96,230,0.18)"))
        color(Color("rgba(59,70,212,0.95)"))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexShrink(0)
        property("cursor", "default")
    }

    val dossierHeaderAssigneeCircleText by style {
        fontSize(13.px)
        fontWeight(700)
        property("letter-spacing", "0.02em")
        property("line-height", "1")
    }

    /** Section Ouvert · date — rapprochée du titre, micro-contexte pour ne pas flotter */
    val dossierStateSection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        marginTop(16.px)
        padding(10.px, 12.px)
        backgroundColor(Color("rgba(15,23,42,0.025)"))
        property("border", "1px solid rgba(15,23,42,0.06)")
        borderRadius(10.px)
        minWidth(0.px)
    }

    /** Spacer vertical entre sections (séparation visuelle sans trait) */
    val dossierSpacer by style {
        height(gapSpacer)
        minWidth(0.px)
        flexShrink(0)
    }

    val dossierHeaderTitle by style {
        fontSize(16.px)
        fontWeight(700)
        color(Color("rgba(15,23,42,0.92)"))
        property("letter-spacing", "-0.012em")
        lineHeight("1.28")
    }

    val dossierHeaderSubtitle by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        property("letter-spacing", "-0.004em")
    }

    val stateHeaderRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.FlexStart)
        minWidth(0.px)
    }

    val stateDateRight by style {
        fontSize(11.5.px)
        fontWeight(500)
        color(Color(AppStylesTokens.INK_MUTED))
        opacity(0.85)
        flexShrink(0)
        property("white-space", "nowrap")
    }

    /** Ligne [dot] Statut à gauche, date à droite — espacement clair */
    val stateRowWithDate by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(14.px)
        minWidth(0.px)
    }

    /** Bloc État + Dernière activité — espacement aéré */
    val stateBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        marginTop(8.px)
    }

    val stateRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.FlexStart)
        gap(10.px)
    }

    /** État Ouvert — discret (aligné liste) */
    val stateDotOpen by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(59,70,212,0.5)"))
        property("box-shadow", "0 0 0 4px rgba(59,70,212,0.08)")
        marginTop(5.px)
    }

    val stateDotWaiting by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(245,158,11,0.65)"))
        property("box-shadow", "0 0 0 4px rgba(245,158,11,0.12)")
        marginTop(5.px)
    }

    val stateDotClosed by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(15,23,42,0.22)"))
        property("box-shadow", "0 0 0 4px rgba(15,23,42,0.06)")
        marginTop(5.px)
    }

    val stateText by style {
        fontSize(14.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_SOFT))
        lineHeight(21.px)
    }


    /** Bloc « Aucun ticket sélectionné » : titre + sous-titre quand tout est analysé et rien de sélectionné */
    val noSelectionCard by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        padding(20.px)
    }

    /** Fade-in doux quand on affiche « Aucun ticket sélectionné » après la transition 100 % */
    val noSelectionCardFadeIn by style {
        property("animation", "${AppStylesAnimations.fadeIn.name} 0.4s ease-out forwards")
    }

    /** Bloc « Boîte prête » affiché 1–2 s à 100 %, puis fade-out */
    val readyCard by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        padding(24.px)
        property("transition", "opacity 0.4s ease-out")
    }

    val readyCardFadeOut by style {
        property("opacity", "0")
    }

    val readyCardTitle by style {
        fontSize(15.px)
        fontWeight(700)
        color(Color("rgba(15, 23, 42, 0.92)"))
        property("letter-spacing", "-0.012em")
    }

    val noSelectionTitle by style {
        fontSize(15.px)
        fontWeight(600)
        color(Color("rgba(15, 23, 42, 0.88)"))
        property("letter-spacing", "-0.01em")
    }

    val noSelectionSubtitle by style {
        fontSize(13.px)
        fontWeight(400)
        color(Color(AppStylesTokens.INK_SOFT))
        property("line-height", "1.4")
    }

    /** Bloc « Analyse des conversations » — version premium : hiérarchie claire, espace, ton factuel */
    val analysisProgressCard by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(24.px)
    }

    val analysisProgressHeader by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        gap(10.px)
    }

    /** Point animé discret (remplace l’emoji cerveau) */
    val analysisProgressDot by style {
        width(8.px)
        height(8.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(102, 96, 230, 0.88)"))
        property("animation", "${AppStylesAnimations.thinkingPulse.name} 0.95s ease-in-out infinite")
        flexShrink(0)
    }

    val analysisProgressTitle by style {
        fontSize(15.px)
        fontWeight(700)
        color(Color("rgba(15, 23, 42, 0.92)"))
        property("letter-spacing", "-0.012em")
    }

    val analysisProgressCounts by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val analysisProgressLineItem by style {
        fontSize(13.px)
        fontWeight(500)
        color(Color("rgba(15, 23, 42, 0.72)"))
        property("line-height", "1.35")
    }

    /** Ligne « ✔ X contextes prêts » : même couleur que le dot (violet) */
    val analysisProgressLineItemReady by style {
        color(Color("rgba(102, 96, 230, 0.88)"))
    }

    /** Ligne fine continue, très discrète, animée lentement */
    val analysisProgressLine by style {
        height(1.px)
        width(100.percent)
        backgroundColor(Color("rgba(15, 23, 42, 0.08)"))
        property("animation", "${AppStylesAnimations.linePulse.name} 2.5s ease-in-out infinite")
    }

    val metaLine by style {
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
    }

    val clientBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }

    val clientEmail by style {
        fontSize(14.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
    }

    val clientMeta by style {
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
    }

    val linkList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val linkRow by style {
        fontSize(13.px)
        fontWeight(650)
        color(Color("rgba(102,96,230,0.85)"))
        property("cursor", "pointer")
        property("transition", "opacity 140ms ease, transform 140ms ease")
    }

    val metaBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        fontSize(13.px)
        fontWeight(650)
        color(Color(AppStylesTokens.INK_SOFT))
    }

    val insightBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        paddingTop(4.px)
    }

    val insightRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
    }

    val insightLabel by style {
        fontSize(11.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        flexShrink(0)
    }

    val insightValue by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_SOFT))
        property("text-align", "right")
    }

    val insightValueMuted by style {
        fontSize(12.px)
        fontWeight(500)
        color(Color(AppStylesTokens.INK_MUTED))
    }

    val ctaWrap by style {
        marginTop(4.px)
    }

    /** Contenu du bouton CTA avec spinner : flex centre + gap */
    val ctaButtonContent by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(8.px)
    }

    /** CTA — aligné à gauche, pleine largeur, espace au-dessus */
    val stateBlockCtaWrap by style {
        marginTop(gapBeforeButton)
        paddingTop(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Stretch)
        gap(10.px)
        minWidth(0.px)
        width(100.percent)
    }

    val ctaSecondary by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        backgroundColor(Color("transparent"))
        border(0.px, LineStyle.Solid, Color("transparent"))
        property("cursor", "pointer")
        property("text-decoration", "underline")
    }

    /** Label au-dessus du bouton : « Ce ticket est en cours chez Alice » */
    val takeoverLabel by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color("rgba(15,23,42,0.75)"))
        lineHeight("1.35")
    }

    /** Bouton Reprendre — même couleur que le fond « À compléter » (orange) */
    val btnReprendre by style {
        padding(10.px, 18.px)
        borderRadius(16.px)
        fontSize(14.px)
        fontWeight(700)
        backgroundColor(Color("rgba(180,83,9,0.30)"))
        color(Color("#fff"))
        property("border", "1px solid rgba(154,70,8,0.5)")
        property("box-shadow", "0 2px 6px rgba(180,83,9,0.2)")
        property("cursor", "pointer")
        property("transition", AppStylesTokens.TRANSITION_FAST)
        self + hover style {
            backgroundColor(Color("rgba(190,90,12,0.55)"))
            property("transform", "translateY(-1px)")
        }
        self + active style { property("transform", "translateY(0)") }
        self + focus style { property("outline", "none"); property("box-shadow", AppStylesTokens.FOCUS_RING) }
    }

    /** Carte Client : email + nom (compact) */
    val clientCard by style {
        padding(14.px, 16.px)
        borderRadius(14.px)
        backgroundColor(Color("rgba(102,96,230,0.06)"))
        border(1.px, LineStyle.Solid, Color("rgba(102,96,230,0.12)"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }

    val clientCardName by style {
        fontSize(14.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
    }

    val clientCardEmail by style {
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        property("word-break", "break-all")
    }

    val clientLink by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color("rgba(102,96,230,0.85)"))
        property("cursor", "pointer")
        marginTop(6.px)
    }

    /** Mémoire : liste verticale premium (label gris, valeur semi-bold, fine separator) */
    val memoryList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
    }

    val memoryListItem by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Baseline)
        gap(12.px)
        minWidth(0.px)
        padding(12.px, 0.px)
        property("border-bottom", "1px solid rgba(15,23,42,0.05)")
        property("transition", "opacity 180ms ease")
        self + lastChild style {
            property("border-bottom", "none")
        }
    }

    val memoryListLabel by style {
        fontSize(12.px)
        fontWeight(600)
        color(Color(AppStylesTokens.INK_MUTED))
        flexShrink(0)
        opacity(1.0)
    }

    val memoryListValue by style {
        fontSize(13.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
        property("text-align", "right")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
        lineHeight("1.4")
    }

    val memoryListValueMissing by style {
        fontSize(13.px)
        fontWeight(700)
        color(Color("rgba(154,70,8,0.92)"))
        backgroundColor(Color("rgba(180,83,9,0.08)"))
        padding(3.px, 8.px)
        borderRadius(6.px)
        property("text-align", "right")
    }

    /** Blocages : titre discret + liste bullet orange */
    val blockageTitle by style {
        fontSize(11.5.px)
        fontWeight(650)
        color(Color(AppStylesTokens.INK_MUTED))
        marginBottom(8.px)
    }

    val blockageBulletList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val blockageBulletItem by style {
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.STATUS_WAITING))
        paddingLeft(4.px)
    }

    // ---------- À compléter pour avancer (checklist soignée) ----------
    val completeCard by style {
        padding(16.px, 18.px)
        borderRadius(AppStylesTokens.RADIUS_CARD.px)
        backgroundColor(Color("rgba(180,83,9,0.04)"))
        border(1.px, LineStyle.Solid, Color("rgba(180,83,9,0.14)"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
    }

    /** Bloc « À compléter » — fond dédié (zone d’action), aligné bord card */
    val completeCardInline by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        marginTop(gapBlock)
        padding(14.px, 16.px)
        backgroundColor(Color("rgba(180,83,9,0.06)"))
        property("border", "1px solid rgba(180,83,9,0.12)")
        borderRadius(12.px)
        minWidth(0.px)
    }

    val completeCardTitle by style {
        fontSize(12.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
        property("letter-spacing", "-0.01em")
        marginBottom(0.px)
    }

    val completeChecklist by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }

    val completeCheckItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        fontSize(12.5.px)
        fontWeight(600)
        color(Color("rgba(154,70,8,0.9)"))
        padding(4.px, 0.px)
        property("transition", "opacity 180ms ease")
    }

    /** Point « requis » (missing) — orange marqué */
    val completeCheckIconMissing by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(180,83,9,0.65)"))
        property("box-shadow", "0 0 0 4px rgba(180,83,9,0.10)")
        flexShrink(0)
    }

    /** Point « à confirmer » (to confirm) — orange plus doux */
    val completeCheckIconToConfirm by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(180,83,9,0.35)"))
        property("box-shadow", "0 0 0 4px rgba(180,83,9,0.08)")
        flexShrink(0)
    }

    /** Legacy (si besoin d’un seul style) */
    val completeCheckIcon by style {
        width(10.px)
        height(10.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(180,83,9,0.65)"))
        property("box-shadow", "0 0 0 4px rgba(180,83,9,0.10)")
        flexShrink(0)
    }

    // ---------- Client (allégé visuellement) ----------
    val clientCardLight by style {
        padding(14.px, 16.px)
        borderRadius(AppStylesTokens.RADIUS_CARD.px)
        backgroundColor(Color("rgba(15,23,42,0.02)"))
        border(1.px, LineStyle.Solid, Color("rgba(15,23,42,0.06)"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }

    val panelTitleSecondary by style {
        fontSize(12.5.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
        property("letter-spacing", "-0.008em")
    }

    val clientCardNameLight by style {
        fontSize(14.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
        lineHeight("1.4")
    }

    val clientCardEmailLight by style {
        fontSize(12.5.px)
        fontWeight(500)
        color(Color(AppStylesTokens.INK_MUTED))
        property("word-break", "break-all")
        lineHeight("1.4")
    }

    val clientLinkLight by style {
        fontSize(11.5.px)
        fontWeight(600)
        color(Color("rgba(102,96,230,0.8)"))
        property("cursor", "pointer")
        marginTop(8.px)
        border(0.px, LineStyle.Solid, Color("transparent"))
        backgroundColor(Color("transparent"))
        padding(0.px)
        property("text-align", "left")
    }

    // ---------- Dossier — premium 10/10 : divider jamais collé, respiration ----------
   

    /** Card Client — discret (contexte), moins marquée que « À compléter » */
    val dossierSection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        marginTop(gapBlock)
        marginLeft(0.px)
        marginRight(0.px)
        paddingTop(14.px)
        paddingBottom(14.px)
        paddingLeft(14.px)
        paddingRight(14.px)
        property("background", "rgba(102,96,230,0.06)")
        property("border", "1px solid rgba(15,23,42,0.05)")
        borderRadius(12.px)
        property("transition", "background-color 180ms ease, border-color 180ms ease")
        self + firstChild style {
            marginTop(0.px)
        }
    }

    /** Section plus proche du bloc au-dessus (ex. Client juste sous Statut) */
    val dossierSectionTight by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        marginTop(14.px)
        marginLeft(0.px)
        marginRight(0.px)
        paddingTop(14.px)
        paddingBottom(14.px)
        paddingLeft(14.px)
        paddingRight(14.px)
        backgroundColor(Color("rgba(15,23,42,0.015)"))
        property("border", "1px solid rgba(15,23,42,0.05)")
        borderRadius(12.px)
        property("transition", "background-color 180ms ease, border-color 180ms ease")
        self + firstChild style {
            marginTop(0.px)
        }
    }

    /** Section Client : sans fond ni bordure, padding réduit */
    val dossierSectionClient by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        marginTop(10.px)
        marginLeft(0.px)
        marginRight(0.px)
        paddingTop(8.px)
        paddingBottom(8.px)
        paddingLeft(8.px)
        paddingRight(8.px)
        backgroundColor(Color("transparent"))
        property("border", "none")
        self + firstChild style {
            marginTop(0.px)
        }
    }

    /** Bloc Résumé dans la sidebar ticket */
    val dossierSummarySection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        marginTop(gapBlock)
        marginLeft(0.px)
        marginRight(0.px)
        paddingTop(14.px)
        paddingBottom(14.px)
        paddingLeft(14.px)
        paddingRight(14.px)
        backgroundColor(Color("rgba(15,23,42,0.015)"))
        property("border", "1px solid rgba(15,23,42,0.05)")
        borderRadius(12.px)
        self + firstChild style {
            marginTop(0.px)
        }
    }

    val dossierSummaryText by style {
        fontSize(13.px)
        lineHeight(20.px)
        fontWeight(400)
        color(Color(AppStylesTokens.INK))
        property("white-space", "normal")
    }

    val dossierSummaryTextEmpty by style {
        fontSize(12.5.px)
        fontWeight(500)
        color(Color(AppStylesTokens.INK_MUTED))
        property("font-style", "italic")
    }

    /** Aligné avec le bord gauche de la card Client (pas de padding latéral) */
    val dossierDataSection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(gapSmall)
        paddingTop(0.px)
        paddingBottom(4.px)
        paddingLeft(0.px)
        paddingRight(0.px)
        backgroundColor(Color("transparent"))
        marginTop(gapBlock)
        property("transition", "background-color 180ms ease")
        self + firstChild style {
            marginTop(0.px)
        }
    }

    val dossierSubtitle by style {
        fontSize(12.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK_SOFT))
        property("letter-spacing", "-0.008em")
        marginBottom(0.px)
    }

    val clientInfoGroup by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
    }

    /** Legacy (fallback) */
    val dataRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        fontSize(13.px)
        fontWeight(600)
    }

    val dataLabel by style {
        color(Color(AppStylesTokens.INK_MUTED))
        flexShrink(0)
    }

    val dataValue by style {
        color(Color(AppStylesTokens.INK_SOFT))
        property("text-align", "right")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }

    val dataValueMissing by style {
        color(Color(AppStylesTokens.STATUS_WAITING))
    }

    val blockageList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }

    val blockageItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        fontSize(12.5.px)
        fontWeight(600)
        color(Color(AppStylesTokens.STATUS_WAITING))
    }

    val ctaButton by style {
        width(100.percent)
        padding(12.px)
        borderRadius(16.px)
        fontSize(14.px)
        fontWeight(750)
        color(Color("white"))
        backgroundColor(Color(AppStylesTokens.PURPLE))
        border(0.px, LineStyle.Solid, Color("transparent"))
        property("cursor", "pointer")
        property("transition", "transform 140ms ease, box-shadow 140ms ease")
        property("box-shadow", "0 14px 34px rgba(102,96,230,0.25)")
    }
}
